/**
 * AdminTeam — manage the Team page without a code change.
 *
 * Route: /admin/team  (auth-gated: user.is_admin)
 * Built-in roster (names, roles, bios, default photos) lives in data/content.js;
 * this page writes overrides, visibility and admin-added people to
 * /api/admin/team/* which /team merges on load.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, Camera, UserPlus } from "lucide-react";

import { useAuth } from "../lib/AuthContext";
import { Navbar } from "../components/Navbar";
import { EmSwash } from "../components/EmSwash";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { useNoIndex } from "../lib/useNoIndex";
import { memberKey, buildRoster, EMPTY_TEAM_DATA } from "../lib/team";
import { TeamPhotoCard } from "../components/admin/TeamPhotoCard";
import { AddTeamMemberDialog } from "../components/admin/AddTeamMemberDialog";
import { TeamReorderList } from "../components/admin/TeamReorderList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem("mra_jwt") : null;
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

const GroupHeader = ({ title, count, group, onAdd }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-olive">
      {title} <span className="ml-1 font-mono text-ink/70">{count}</span>
    </h2>
    {group && (
      <button type="button" onClick={() => onAdd(group)} data-testid={`team-add-${group}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-ink/25 bg-bone px-4 py-2 text-[12px] font-semibold text-ink transition hover:border-ink">
        <UserPlus className="h-3.5 w-3.5" strokeWidth={2} />
        Add a person
      </button>
    )}
  </div>
);

export default function AdminTeam() {
  useNoIndex();
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);       // {photos, order}
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(null);   // "board" | "advisor" | null

  const refresh = useCallback(async () => {
    try {
      const r = await client.get("/team/photos");
      setData({ photos: r.data.photos || {}, order: r.data.order || {} });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load the team.");
      setData(EMPTY_TEAM_DATA);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login", { replace: true, state: { from: "/admin/team" } });
    else if (!user.is_admin) navigate("/dashboard", { replace: true });
    else refresh();
  }, [authLoading, user, navigate, refresh]);

  const onSaved = (key, record) => setData((prev) => ({ ...prev, photos: { ...prev.photos, [key]: record } }));
  const onRemoved = (key) => setData((prev) => { const photos = { ...prev.photos }; delete photos[key]; return { ...prev, photos }; });
  const onOrderSaved = (order) => setData((prev) => ({ ...prev, order }));

  if (authLoading || !user || !user.is_admin) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sand">
        <Loader2 className="h-6 w-6 animate-spin text-olive" />
      </main>
    );
  }

  const roster = data ? buildRoster(data, { includeHidden: true }) : null;
  const groups = roster ? [
    { title: "Founder", people: [roster.founder], group: null },
    { title: "Board of directors", people: roster.board, group: "board" },
    { title: "Advisory board", people: roster.advisors, group: "advisor" },
  ] : [];
  const takenKeys = new Set(groups.flatMap((g) => g.people.map((p) => memberKey(p.name))));

  return (
    <main data-testid="admin-team-page" className="bg-sand">
      <Navbar onStart={async () => { await logout(); navigate("/"); }} primaryLabel="Sign out" secondaryLabel={null} />

      <section className="mx-auto max-w-5xl px-6 pb-24 pt-32 lg:px-8 lg:pt-44">
        <FadeUp tier="sm"><PageAccent className="mb-5" /></FadeUp>
        <FadeUp>
          <button type="button" onClick={() => navigate("/admin")} data-testid="admin-team-back"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-olive transition hover:text-ink">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            back to admin
          </button>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-cobalt">
            <Camera className="h-3.5 w-3.5" strokeWidth={2} />
            Team page
          </span>
          <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">
            Team page: <EmSwash>people, photos &amp; bios</EmSwash>.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-olive">
            Add a new board member or advisor, drag the cards into the order you want, hide someone who has stepped down,
            drop in a new &ldquo;today&rdquo; or &ldquo;then&rdquo; photo, or rewrite a role and bio. Photos are resized and cleaned automatically; use the focus slider to keep the face clear of the small polaroid.
            Changes are live on <a href="/team" target="_blank" rel="noreferrer" className="underline decoration-ink/30 underline-offset-2 hover:text-ink">/team</a> immediately.
          </p>
        </FadeUp>

        {error && <p className="mt-6 text-[13px] text-scarlet" data-testid="admin-team-error">{error}</p>}

        {data === null ? (
          <div className="mt-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-olive" /></div>
        ) : (
          groups.map((g) => {
            const card = (p) => {
              const key = memberKey(p.name);
              return (
                <TeamPhotoCard key={key} person={p} memberKey={key} override={data.photos[key]} client={client}
                  onSaved={onSaved} onRemoved={onRemoved} canHide={Boolean(g.group)} />
              );
            };
            return (
              <div key={g.title} className="mt-12" data-testid={`admin-team-group-${g.group || "founder"}`}>
                <GroupHeader title={g.title} count={g.people.filter((p) => !p.hidden).length} group={g.group} onAdd={setAdding} />
                {g.group ? (
                  <TeamReorderList group={g.group} people={g.people} client={client} onOrderSaved={onOrderSaved} renderCard={card} />
                ) : (
                  <div className="mt-4 space-y-4">{g.people.map(card)}</div>
                )}
                {g.people.length === 0 && <p className="mt-4 text-[13px] italic text-olive">Nobody here yet.</p>}
              </div>
            );
          })
        )}
      </section>

      {adding && (
        <AddTeamMemberDialog
          open onOpenChange={(o) => { if (!o) setAdding(null); }}
          group={adding}
          people={adding === "board" ? roster.board : roster.advisors}
          takenKeys={takenKeys}
          client={client}
          onCreated={onSaved}
        />
      )}

      <Footer variant="minimal" />
    </main>
  );
}
